import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { DropzoneInputProps } from 'react-dropzone';
import { FieldRenderProps, useForm } from 'react-final-form';
import Dropzone from '../Dropzone';
import { DropzoneFieldRequests } from '../../hooks/useDropzoneField';
import { DropzoneFile } from '../Dropzone/utils';
import FieldWrapper from 'components/FieldWrapper';
import { FieldWrapperProps } from 'components/FieldWrapper/FieldWrapper.component';

import './DropzoneField.styles.scss';

export type DropzoneFieldProps = FieldWrapperProps & {
  tooltip?: React.FC;
  fileName?: string;
  showApplicationAsIframe?: boolean;
  onFileDelete?: () => void;
} & FieldRenderProps<DropzoneFile[], HTMLElement> &
  Omit<DropzoneFieldRequests, 'fetchFiles'> &
  DropzoneInputProps;

const DropzoneField: React.FC<DropzoneFieldProps> = (props) => {
  const {
    fileName: pFileName,
    className,
    label,
    required,
    description,
    input: { onChange, value: formValue, name },
    meta: { touched, error },
    tooltip,
    maxFiles,
    ...rest
  } = props;
  const hasError = useMemo(() => touched && error, [error, touched]);
  const { change } = useForm();

  const value = useMemo(
    () =>
      formValue ? (Array.isArray(formValue) ? formValue : [formValue]) : [],
    [formValue],
  );

  const fileName = useMemo(
    () => (pFileName ? pFileName : name),
    [name, pFileName],
  );

  const handleFileDelete = useCallback(
    (dropzoneFile: DropzoneFile) => {
      const newValue =
        maxFiles === 1
          ? undefined
          : value.filter((file: DropzoneFile) =>
              file.id
                ? file.id !== dropzoneFile.id
                : file.key !== dropzoneFile.key,
            );

      change(name, newValue);
    },
    [change, maxFiles, name, value],
  );

  const onChangeHandle = useCallback(
    (filesArray: DropzoneFile[]) => {
      if (filesArray?.length === 1) {
        onChange(filesArray[0]);
      } else {
        onChange(filesArray);
      }
    },
    [onChange],
  );

  const classes = classNames('dropzone-field', className);

  return (
    <FieldWrapper
      className={classes}
      error={error}
      hasError={hasError}
      name={name}
      label={label}
      required={required}
      description={description}
    >
      <Dropzone
        {...rest}
        className="dropzone-field__container__dropzone"
        files={value}
        fileName={fileName}
        maxFiles={maxFiles}
        onChange={onChangeHandle}
        onFileDelete={handleFileDelete}
      />
    </FieldWrapper>
  );
};
export default DropzoneField;
